export const SUPPORTED_LOCALES = ["hr", "en", "de", "es", "fr", "it"] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const DEFAULT_LOCALE: SupportedLocale = "hr";
const USER_LOCALE_STORAGE_KEY = "user-locale";
const URL_LOCALE_PARAMS = ["lang", "locale", "hl"] as const;
const PATH_LOCALE_PATTERN = /^\/(hr|en)(?:\/|$)/i;

const COUNTRY_TO_LOCALE: Record<string, SupportedLocale> = {
  HR: "hr",
  BA: "hr",
  RS: "hr",
  // DE: "de",
  // AT: "de",
};

const LANGUAGE_TO_LOCALE: Record<string, SupportedLocale> = {
  bs: "hr",
  sr: "hr",
};

const normalize = (value: string | null | undefined): string => (value ?? "").trim().toLowerCase();

const asSupported = (value: string | null | undefined): SupportedLocale | null => {
  const normalized = normalize(value);
  if (!normalized) return null;

  if (SUPPORTED_LOCALES.includes(normalized as SupportedLocale)) {
    return normalized as SupportedLocale;
  }

  const language = normalized.split(/[-_]/)[0];
  if (SUPPORTED_LOCALES.includes(language as SupportedLocale)) {
    return language as SupportedLocale;
  }

  return null;
};

const mapLanguage = (value: string | null | undefined): SupportedLocale | null => {
  const normalized = normalize(value);
  if (!normalized) return null;

  const language = normalized.split(/[-_]/)[0];
  const mapped = LANGUAGE_TO_LOCALE[language];
  if (mapped) return mapped;

  return asSupported(normalized);
};

const mapCountryToLocale = (countryCode: string | null | undefined): SupportedLocale | null => {
  const country = (countryCode ?? "").trim().toUpperCase();
  if (!country) return null;
  return COUNTRY_TO_LOCALE[country] ?? null;
};

const getSavedUserLocale = (): SupportedLocale | null => {
  if (typeof window === "undefined") return null;
  return asSupported(window.localStorage.getItem(USER_LOCALE_STORAGE_KEY));
};

const getUrlOverrideLocale = (urlSearch: string | null | undefined): SupportedLocale | null => {
  if (!urlSearch) return null;

  const params = new URLSearchParams(urlSearch);
  for (const param of URL_LOCALE_PARAMS) {
    const value = params.get(param);
    const mapped = mapLanguage(value);
    if (mapped) return mapped;
  }

  return null;
};

export const getPathLocale = (pathname: string | null | undefined): SupportedLocale | null => {
  const match = (pathname ?? "").match(PATH_LOCALE_PATTERN);
  return match ? asSupported(match[1]) : null;
};

const getGeoHintLocale = (geoCountryHint: string | null | undefined): SupportedLocale | null => mapCountryToLocale(geoCountryHint);

const getBrowserLocale = (languages: readonly string[] | null | undefined): SupportedLocale | null => {
  if (!languages?.length) return null;

  for (const browserLanguage of languages) {
    const mappedByLanguage = mapLanguage(browserLanguage);
    if (mappedByLanguage) return mappedByLanguage;

    const country = browserLanguage.split(/[-_]/)[1];
    const mappedByCountry = mapCountryToLocale(country);
    if (mappedByCountry) return mappedByCountry;
  }

  return null;
};

const readGeoHint = (): string | null => {
  if (typeof window === "undefined") return null;

  const globalHint = window.__MSQ_GEO_COUNTRY__;
  if (typeof globalHint === "string" && globalHint.trim()) {
    return globalHint;
  }

  const documentHint = document.documentElement.getAttribute("data-geo-country");
  if (documentHint) return documentHint;

  return null;
};

export const resolveLocale = (params?: {
  savedLocale?: string | null;
  pathname?: string | null;
  urlSearch?: string;
  geoCountryHint?: string | null;
  browserLanguages?: readonly string[];
  fallbackLocale?: SupportedLocale;
}): SupportedLocale => {
  const fallbackLocale = params?.fallbackLocale ?? DEFAULT_LOCALE;

  const fromPath = getPathLocale(params?.pathname);
  if (fromPath) return fromPath;

  const fromSaved = mapLanguage(params?.savedLocale ?? null);
  if (fromSaved) return fromSaved;

  const fromUrl = getUrlOverrideLocale(params?.urlSearch);
  if (fromUrl) return fromUrl;

  const fromGeo = getGeoHintLocale(params?.geoCountryHint ?? null);
  if (fromGeo) return fromGeo;

  const fromBrowser = getBrowserLocale(params?.browserLanguages);
  if (fromBrowser) return fromBrowser;

  return fallbackLocale;
};

export const resolveLocaleForRuntime = (): SupportedLocale => {
  const savedLocale = getSavedUserLocale();
  const pathname = typeof window === "undefined" ? "" : window.location.pathname;
  const urlSearch = typeof window === "undefined" ? "" : window.location.search;
  const geoCountryHint = readGeoHint();
  const browserLanguages = typeof navigator === "undefined" ? [] : navigator.languages;

  return resolveLocale({
    savedLocale,
    pathname,
    urlSearch,
    geoCountryHint,
    browserLanguages,
    fallbackLocale: DEFAULT_LOCALE,
  });
};

export const persistUserLocale = (locale: string): SupportedLocale => {
  const mappedLocale = mapLanguage(locale) ?? DEFAULT_LOCALE;

  if (typeof window !== "undefined") {
    window.localStorage.setItem(USER_LOCALE_STORAGE_KEY, mappedLocale);
  }

  return mappedLocale;
};

export const getDefaultLocale = (): SupportedLocale => DEFAULT_LOCALE;
